// hooks/useChatSessions.js
// ------------------------
// Custom React hook that owns ALL session and messaging state.
//
// Responsibilities:
//   - Keeping the list of sessions and the active session in state
//   - Creating a new session on the backend
//   - Fetching all sessions for the current user
//   - Fetching messages for a specific session
//   - Sending a new chat message and storing the AI reply
//   - Deleting (clearing) the active session's messages
//
// Why a custom hook?
//   The component that renders the UI doesn't need to know HOW the
//   backend is called — it just calls functions and reads state.
//   Putting all of this in a hook keeps EmbeddedChatAgents.jsx short
//   and focused on layout only.

import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import agents from '../config/agents';
import { getUserId, mapBackendMessagesToReachat } from '../utils/chatHelpers';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Module-level guard — survives React StrictMode double-mount because it lives
// outside the hook function. Key = "visitorId|agentType|botId", value = Promise.
const _inflightFetch = new Map();

/**
 * @param {string}   agentType    - Active agent type (e.g. "knowledgebase_agent")
 * @param {string}   botId        - Agent instance ID from URL params
 * @param {string[]} pickedFileIds - IDs of files the user has selected
 * @param {object}   chatContext  - Extra context payload sent to the backend with each message
 * @param {string|null} visitorId - Visitor ID from URL params (null = anonymous)
 */
const useChatSessions = ({ agentType, botId, pickedFileIds, chatContext, visitorId }) => {
  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [currentSessionMessages, setCurrentSessionMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  // How far the Document Finder has got with the message it is answering (GET /chat/progress), shown in
  // the typing bubble while the person waits. null when nothing is running.
  const [progress, setProgress] = useState(null);
  const [sessionError, setSessionError] = useState(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  // Tracks the last failed async operation so the UI's Retry button can
  // replay it without a full page reload. Set inside the catch blocks of
  // the three startup/history fetches below; cleared on success.
  const retryActionRef = useRef(null);
  const [hasRetry, setHasRetry] = useState(false);
  const _markRetry = (fn) => { retryActionRef.current = fn; setHasRetry(true); };
  const _clearRetry = () => { retryActionRef.current = null; setHasRetry(false); };

  const retrySessionAction = useCallback(async () => {
    const fn = retryActionRef.current;
    if (!fn) return;
    setSessionError(null);
    _clearRetry();
    try { await fn(); } catch (_e) { /* error path sets its own retry */ }
  }, []);

  // Heuristic — treat any request without a response as a network error
  const _isNetworkError = (error) => {
    return !error?.response && (
      error?.code === 'ERR_NETWORK' ||
      error?.code === 'ECONNABORTED' ||
      error?.message === 'Network Error' ||
      error?.name === 'TypeError'
    );
  };

  // Ref used by getPayload (kept from original code)
  const messagesPayload = useRef({});
  // AbortController ref — lets us cancel the in-flight /chat POST (TC_009/TC_012)
  const abortControllerRef = useRef(null);
  // Polling ref — polls for bot reply when user navigates away mid-request
  const pollingRef = useRef(null);


  const getPayload = useCallback(() => {
    return messagesPayload;
  }, []);

  // Note: live agent-step polling is handled directly inside TypingIndicator
  // using the sessionId prop — no hook state needed.

  // -------------------------------------------------------------------------
  // createNewBackendSession
  // -------------------------------------------------------------------------
  // Calls GET /creat_sessions, then refreshes the session list and marks the
  // new session as active.
  const createNewBackendSession = async (updatedSessions = null) => {
    if (updatedSessions == null) {
      updatedSessions = sessions;
    }
    setLoading(true);
    setIsCreatingSession(true);
    try {
      const userId = getUserId(visitorId, botId);
      const workspaceId = chatContext?.workspaceId || '';
      const response = await axios.get(
        `${API_BASE_URL}/creat_sessions?visitorId=${userId}&agentType=${agentType}&botId=${botId}&pickedFiles=${pickedFileIds.join(',')}&workspace_id=${workspaceId}`
      );
      const data = response.data;

      // Add the new session directly to state — no need to re-fetch all sessions
      // (avoids a recursive fetchAllSessions → createNewBackendSession loop)
      const newSession = {
        id: data.session_id,
        title: `Session ${data.session_id.substring(0, 4)}`,
        createdAt: new Date(),
        updatedAt: new Date(),
        conversations: [],
      };
      setSessions(prev => [...prev, newSession]);

      // Activate the new session and load its messages (welcome message is
      // already saved in MongoDB by the backend at creation time, so this
      // fetch will always return at least the welcome bubble).
      setActiveSessionId(data.session_id);
      // Drop any prior retry bubble from a previous failed start
      setCurrentSessionMessages(prev => prev.filter(
        m => m?.id !== 'history-retry' && m?.id !== 'newsession-retry'));
      await fetchMessagesForSession(data.session_id);

      console.log('New session created:', data.session_id);
      setSessionError(null);
      _clearRetry();
      return;
    } catch (error) {
      console.error('Error creating new session on backend:', error);
      const net = _isNetworkError(error);
      const errorText = net
        ? 'Connection lost while starting the chat. Check your network and tap Retry.'
        : 'Failed to start a chat session. Tap Retry to try again.';
      // Full-screen error panel only fires for first-time load (sessions
      // empty). For later failures (e.g. during "Start New Conversation"
      // when server is down) inject an inline retry bubble so the chat
      // window surfaces the error rather than showing a blank state.
      setSessionError(errorText);
      setCurrentSessionMessages(prev => ([
        ...prev.filter(m => m?.id !== 'history-retry' && m?.id !== 'newsession-retry'),
        mapBackendMessagesToReachat([{
          id: 'newsession-retry',
          text: { kind: 'error_retry', payload: errorText },
          sender: 'system',
          timestamp: new Date().toISOString(),
        }])[0],
      ]));
      _markRetry(() => createNewBackendSession(updatedSessions));
      return null;
    } finally {
      setLoading(false);
      setIsCreatingSession(false);
    }
  };

  // -------------------------------------------------------------------------
  // fetchAllSessions
  // -------------------------------------------------------------------------
  // Loads all sessions for this user+agent from the backend.
  // Auto-selects the most recent session (or creates one if none exist).
  const fetchAllSessions = async () => {
    const userId = getUserId(visitorId, botId);
    const guardKey = `${userId}|${agentType}|${botId}`;

    // If an identical fetch is already in-flight (React StrictMode double-mount),
    // wait for it to finish rather than firing a second one.
    if (_inflightFetch.has(guardKey)) {
      await _inflightFetch.get(guardKey);
      return;
    }

    setLoading(true);
    const promise = (async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/sessions?visitorId=${userId}&agentType=${agentType}&botId=${botId}`
        );
        const backendSessions = response.data;

        const mappedSessions = backendSessions.map(s => ({
          id: s.session_id,
          title: s.title || `Session ${s.session_id.substring(0, 4)}`,
          createdAt: new Date(s.created_at),
          updatedAt: new Date(s.last_activity),
          conversations: [],
        }));

        mappedSessions.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
        setSessions(mappedSessions);

        if (mappedSessions.length > 0) {
          // Automatically select the last (most recent) session
          const lastSessionId = mappedSessions[mappedSessions.length - 1].id;
          setActiveSessionId(lastSessionId);
          await fetchMessagesForSession(lastSessionId);
        } else {
          // No sessions exist yet for this user — create the first one
          console.log('No existing sessions found for this user, creating a new one.');
          await createNewBackendSession();
        }
        _clearRetry();
      console.log(`Fetched ${mappedSessions.length} sessions from backend for user: ${userId}`);
      } catch (error) {
        console.error('Error fetching all sessions:', error);
        const net = _isNetworkError(error);
        setSessionError(net
          ? 'Connection lost while loading sessions. Check your network and retry.'
          : 'Failed to load sessions. Please retry.');
        _markRetry(() => fetchAllSessions());
      } finally {
        setLoading(false);
        _inflightFetch.delete(guardKey);
      }
    })();

    _inflightFetch.set(guardKey, promise);
    await promise;
  };

  // -------------------------------------------------------------------------
  // stopPolling / startPollingForReply
  // -------------------------------------------------------------------------
  // When the user navigates away while /chat is in-flight, the old component
  // unmounts and the axios response is lost.  On remount, fetchMessagesForSession
  // detects that the last message is from the user (bot hasn't replied yet) and
  // starts polling every 2 s until a bot reply arrives.
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // Clean up poller on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  const startPollingForReply = useCallback((sessionId, knownMsgCount) => {
    stopPolling();
    let attempts = 0;
    const MAX_ATTEMPTS = 30; // 30 × 2 s = 60 s max wait, then give up
    pollingRef.current = setInterval(async () => {
      attempts += 1;
      if (attempts > MAX_ATTEMPTS) {
        console.warn('[poll] Max attempts reached — stopping poll for session', sessionId);
        setIsTyping(false);
        setLoading(false);
        stopPolling();
        return;
      }
      try {
        const userId = getUserId(visitorId, botId);
        const res = await axios.get(
          `${API_BASE_URL}/sessions/${sessionId}/messages?visitorId=${userId}&agentType=${agentType}`
        );
        const { messages = [] } = res.data;
        if (messages.length > knownMsgCount) {
          // Bot reply arrived — update messages, stop typing, stop polling
          setCurrentSessionMessages(mapBackendMessagesToReachat(messages));
          setIsTyping(false);
          setLoading(false);
          stopPolling();
        }
      } catch (err) {
        console.error('[poll] Error checking for bot reply:', err);
      }
    }, 2000);
  }, [agentType, botId, visitorId, stopPolling]);

  // -------------------------------------------------------------------------
  // fetchMessagesForSession
  // -------------------------------------------------------------------------
  // Loads all messages for a given session and converts them to Reachat format.
  // Also rebuilds the "conversations" array (user/bot pairs) that Reachat uses
  // internally to render the session in the sidebar.
  // silent=true → skip setLoading(true/false) so the UI never goes blank
  // Used when refreshing messages on bubble-open (messages already visible).
  const fetchMessagesForSession = useCallback(async (sessionId, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const userId = getUserId(visitorId, botId);
      const response = await axios.get(
        `${API_BASE_URL}/sessions/${sessionId}/messages?visitorId=${userId}&agentType=${agentType}`
      );
      const { messages = [], previousSelectedFiles = [] } = response.data;
      setCurrentSessionMessages(mapBackendMessagesToReachat(messages));

      // If the last message is from the user, the bot reply hasn't arrived yet
      // (user navigated away mid-request). Show typing indicator and poll until
      // the reply lands so the conversation resumes seamlessly.
      const lastMsg = messages[messages.length - 1];
      if (lastMsg && lastMsg.sender === 'user') {
        setIsTyping(true);
        startPollingForReply(sessionId, messages.length);
      } else {
        stopPolling();
      }

      // Build Reachat conversation pairs (user message + bot reply = one "conversation")
      let i = 0;
      let newConversations = [];
      while (i < messages.length) {
        const msg1 = messages[i];
        const msg2 = messages[i + 1];

        // A lone bot message (e.g. welcome) with no preceding user message
        if (msg1.sender === 'bot') {
          newConversations.push({
            id: uuidv4(),
            question: '',
            response: msg1.text,
            createdAt: null,
            updatedAt: new Date(msg1.timestamp),
          });
          i++;
          continue;
        }

        // Normal pair: user message followed by bot reply
        if (msg1 && msg2 && msg1.sender === 'user' && msg2.sender === 'bot') {
          newConversations.push({
            id: uuidv4(),
            question: msg1.text,
            response: msg2.text,
            createdAt: new Date(msg1.timestamp),
            updatedAt: new Date(msg2.timestamp),
          });
          i += 2;
        } else {
          i++;
        }
      }
      newConversations = newConversations.reverse();
      setSessions(prevSessions =>
        prevSessions.map(s => {
          if (s.id === sessionId) {
            return { ...s, conversations: newConversations.reverse() };
          }
          return s;
        }).sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
      );

      // Drop any prior retry-bubble from a previous failed attempt
      setCurrentSessionMessages(prev => prev.filter(m => m?.id !== 'history-retry'));
      _clearRetry();
      console.log(`Fetched ${messages.length} messages for session: ${sessionId}`);
    } catch (error) {
      console.error(`Error fetching messages for session ${sessionId}:`, error);
      const net = _isNetworkError(error);
      const errorText = net
        ? "I couldn't reach the server to load your chat history. Check your connection and tap Retry."
        : "Something went wrong loading your chat history. Tap Retry to try again.";
      // Keep the chat window rendering — inject an inline error bubble that
      // carries a retry hook. The ChatMessage renderer reads `kind:'error_retry'`
      // and shows a Retry button that fires retrySessionAction().
      if (!silent) {
        setCurrentSessionMessages(prev => ([
          ...prev.filter(m => m?.id !== 'history-retry'),
          mapBackendMessagesToReachat([{
            id: 'history-retry',
            text: { kind: 'error_retry', payload: errorText },
            sender: 'system',
            timestamp: new Date().toISOString(),
          }])[0],
        ]));
      }
      _markRetry(() => fetchMessagesForSession(sessionId, { silent }));
      if (!silent) setSessionError(null);   // don't blank the screen, keep chat open
    } finally {
      if (!silent) setLoading(false);
    }
  }, [agentType, botId, visitorId, pickedFileIds]);

  // -------------------------------------------------------------------------
  // handleNewSession
  // -------------------------------------------------------------------------
  // Called when the user clicks the "New Session" button.
  const handleNewSession = async () => {
    await createNewBackendSession();
  };

  // -------------------------------------------------------------------------
  // handleSelectSession
  // -------------------------------------------------------------------------
  // Called when the user picks a different session in the sidebar.
  const handleSelectSession = async (id) => {
    setActiveSessionId(id);
    await fetchMessagesForSession(id);
  };

  // -------------------------------------------------------------------------
  // handleSendMessage
  // -------------------------------------------------------------------------
  // Optimistically shows the user's message, then calls POST /chat and appends
  // the AI reply when it arrives.
  const handleSendMessage = async (messageContent) => {
    // Block if loading OR typing (covers rapid multiple quick sends — TC_004)
    if (!activeSessionId || loading || isTyping || !messageContent.trim()) {
      return;
    }

    // ── Quick Form shortcut ────────────────────────────────────────────────
    // When the user clicks "Fill Quick Form" in the visit_choice card, the
    // button fires onSend('__VISIT_FORM__:<property>').  We intercept this
    // here — inject a visit_form card directly into the chat without any
    // backend round-trip, so the form appears instantly.
    if (messageContent.startsWith('__VISIT_FORM__:')) {
      const propertyName = messageContent.replace('__VISIT_FORM__:', '').trim();
      const formMsg = mapBackendMessagesToReachat([{
        id: uuidv4(),
        text: {
          kind: 'visit_form',
          payload: {
            property_name: propertyName,
            agent_key:     botId || '',
            workspace_id:  chatContext?.workspace_id || '',
          },
        },
        sender: 'assistant',
        timestamp: new Date().toISOString(),
      }])[0];
      setCurrentSessionMessages(prev => [...prev, formMsg]);
      return;
    }
    // ── End Quick Form shortcut ────────────────────────────────────────────

    // NOTE: do NOT set loading=true here — loading is only for session/history fetches.
    // Setting it true during a message send causes the Chat component to render a
    // blank white screen when the iframe regains focus mid-request.
    // isTyping=true is sufficient to show the typing indicator while waiting.
    setIsTyping(true);

    // Create a fresh AbortController for this request so it can be cancelled
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Show the user's message immediately (optimistic update)
    const userMessage = {
      id: uuidv4(),
      content: messageContent,
      type: 'user',
      // When it was sent (shown under the message, and where an answer's "took" starts).
      timestamp: new Date().toISOString(),
    };
    const userMsgMapped = mapBackendMessagesToReachat([userMessage]);
    setCurrentSessionMessages(prev => [...prev, userMsgMapped[0]]);

    // The Document Finder can take minutes: ask every 1.5 s how far it has got, for the typing bubble.
    // Only a nicety - a failed poll is ignored, and the answer arrives the same way regardless.
    let progressTimer = null;
    if (agentType === 'idokschat_agent' && activeSessionId) {
      const pollProgress = async () => {
        try {
          const r = await axios.get(`${API_BASE_URL}/chat/progress`, { params: { session_id: activeSessionId } });
          setProgress(r.data || null);
        } catch { /* ignored - see above */ }
      };
      progressTimer = setInterval(pollProgress, 1500);
    }

    try {
      const userId = getUserId(visitorId, botId);
      const response = await axios.post(
        `${API_BASE_URL}/chat?visitorId=${userId}&agentType=${agentType}&botId=${botId}`,
        {
          session_id: activeSessionId,
          message: messageContent,
          chatContext: chatContext,
        },
        { signal: controller.signal }
      );

      const aiResponse = response.data.response;
      // Attach reasoning (DEV_MODE only), suggestions (action chips), and
      // mark isNew=true so the typewriter animation fires on first render.
      const aiMsgMapped = mapBackendMessagesToReachat([{
        ...aiResponse,
        reasoning:   response.data.reasoning   || null,
        suggestions: response.data.suggestions || null,
        howFoundSummary: response.data.howFoundSummary || null,
        fullTrace: response.data.fullTrace || null,
        // When this browser got the answer - paired with the question's own browser time, so "took" is
        // measured on one clock.
        receivedAt: new Date().toISOString(),
        flowSteps: response.data.flowSteps || null,
        isNew: true,
      }])[0];

      setCurrentSessionMessages(prev => [...prev, aiMsgMapped]);
      stopPolling(); // reply arrived — no need to poll

      // The "how I found this" recap is written after the answer is sent. Ask for it every
      // couple of seconds (up to ~30s) and drop it onto this answer when it is ready.
      if (response.data.summaryId && !aiMsgMapped.howFoundSummary) {
        const summaryId = response.data.summaryId;
        const answerId = aiMsgMapped.id;
        let tries = 0;
        const timer = setInterval(async () => {
          tries += 1;
          try {
            const r = await axios.get(`${API_BASE_URL}/chat/summary`, {
              params: { session_id: activeSessionId, message_id: summaryId },
            });
            const text = r.data?.howFoundSummary;
            if (text) {
              clearInterval(timer);
              setCurrentSessionMessages(prev =>
                prev.map(m => (m.id === answerId ? { ...m, howFoundSummary: text } : m)));
              return;
            }
          } catch (e) { /* try again on the next tick */ }
          if (tries >= 15) clearInterval(timer);
        }, 2000);
      }

      // Keep sidebar conversation list in sync
      setSessions(prevSessions =>
        prevSessions.map(s => {
          if (s.id === activeSessionId) {
            const updatedConversations = [...s.conversations];
            updatedConversations.push({
              id: uuidv4(),
              question: userMessage.content,
              response: aiResponse.text,
              createdAt: new Date(userMessage.timestamp),
              updatedAt: new Date(aiResponse.timestamp || new Date().toISOString()),
            });
            return { ...s, updatedAt: new Date(), conversations: updatedConversations };
          }
          return s;
        }).sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
      );
    } catch (error) {
      // Cancelled by user — remove the optimistic user message and stay silent
      if (axios.isCancel(error) || error?.name === 'CanceledError' || error?.name === 'AbortError') {
        setCurrentSessionMessages(prev => prev.filter(m => m.id !== userMessage.id));
        return;
      }

      console.error('Error sending message or getting AI response:', error);

      // Detect error type for user-friendly messages
      const status  = error?.response?.status;
      const errData = error?.response?.data || {};
      const errCode = errData?.error?.code || errData?.code || '';
      const errMsg_ = String(errData?.error?.message || error?.message || '').toLowerCase();

      const isRateLimit =
        status === 429 ||
        errCode === 'rate_limit_exceeded' ||
        errMsg_.includes('rate limit');

      const isSessionExpired =
        status === 404 ||
        status === 410 ||
        errMsg_.includes('session') ||
        errMsg_.includes('expired') ||
        errMsg_.includes('not found');

      const isNetworkError =
        !error?.response && (error?.code === 'ERR_NETWORK' || error?.code === 'ECONNABORTED' || error?.message === 'Network Error');

      const errorText = isRateLimit
        ? "I'm handling a lot of requests right now and need a moment to catch up. Please try again in a few seconds."
        : isSessionExpired
        ? "Your session has expired after being idle. Starting a fresh conversation now..."
        : isNetworkError
        ? "I'm having trouble connecting. Please check your internet connection and try again."
        : 'Something went wrong. Please try again.';

      const errMsg = mapBackendMessagesToReachat([{
        id: uuidv4(),
        text: { kind: 'error', payload: errorText },
        sender: 'system',
        timestamp: new Date().toISOString(),
      }]);
      setCurrentSessionMessages(prev => [...prev, errMsg[0]]);
    } finally {
      if (progressTimer) clearInterval(progressTimer);
      setProgress(null);
      setIsTyping(false);
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // handleDeleteSession
  // -------------------------------------------------------------------------
  // Calls DELETE /clearsessions/{id} to wipe the current session on the
  // backend, then immediately creates a brand-new session so the chat
  // starts completely fresh (new session_id, new welcome message, clean
  // memory context on the backend).
  // Note: closing the menu (setShowMenu) is the caller's responsibility.
  const handleDeleteSession = async () => {
    setLoading(true);
    const previousSessionId = activeSessionId;
    try {
      const userId = getUserId(visitorId, botId);
      await axios.delete(
        `${API_BASE_URL}/clearsessions/${previousSessionId}?visitorId=${userId}&agentType=${agentType}`
      );
      console.log(`Session ${previousSessionId} cleared. Creating a new session...`);

      // Clear UI immediately so the user sees an empty chat right away
      setCurrentSessionMessages([]);

      // Create a fresh session — this sets a new activeSessionId, fetches
      // the welcome message, and refreshes the session list in the sidebar.
      await createNewBackendSession();
    } catch (error) {
      console.error(`Error clearing session ${previousSessionId}:`, error);
      const net = _isNetworkError(error);
      const errorText = net
        ? "I couldn't reach the server to start a new conversation. Check your connection and tap Retry."
        : "Something went wrong starting a new conversation. Tap Retry to try again.";
      // Inject an inline retry bubble so the user sees something in the chat
      // window instead of a blank screen when the server is down.
      setCurrentSessionMessages(prev => ([
        ...prev.filter(m => m?.id !== 'history-retry' && m?.id !== 'newsession-retry'),
        mapBackendMessagesToReachat([{
          id: 'newsession-retry',
          text: { kind: 'error_retry', payload: errorText },
          sender: 'system',
          timestamp: new Date().toISOString(),
        }])[0],
      ]));
      _markRetry(() => handleDeleteSession());
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Return everything the component needs
  // -------------------------------------------------------------------------
  // Cancel the in-flight /chat request (called by the X button — TC_009/TC_012)
  const cancelRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    stopPolling();
    setIsTyping(false);
    setLoading(false);
  };

  return {
    // State
    sessions,
    activeSessionId,
    currentSessionMessages,
    loading,
    isTyping,
    progress,
    sessionError,
    isCreatingSession,
    hasRetry,
    // Handlers
    fetchAllSessions,
    fetchMessagesForSession,
    handleNewSession,
    handleSelectSession,
    handleSendMessage,
    handleDeleteSession,
    cancelRequest,
    getPayload,
    retrySessionAction,
  };
};

export default useChatSessions;
